// src/modals/BookingCancelModal.jsx — Lot annulation résa payée (CHEMIN ARGENT)
//
// Modale d'annulation d'une résa directe PAYÉE — composant PARTAGÉ (décision
// Dimitri 2026-07) entre :
//   - la vue Demandes directes (src/tabs/messages/BookingRequestsView.jsx,
//     livraison d'origine PR #991) — keyée sur `request.id` ;
//   - l'onglet Paiements de la modale calendrier (_PaiementsTabBody.jsx),
//     keyée sur `pricingBlockId` (le serveur résout la demande liée via
//     direct_booking_requests.pricing_block_id et renvoie `request_id`).
//
// Move quasi byte-identique depuis BookingRequestsView.jsx (composant local
// `CancelBookingModal`) — seule addition : le mode `pricingBlockId` (fetch
// info par pricing_block_id + submit avec le request_id résolu par le
// serveur). Les data-testid sont INCHANGÉS.
//
// À l'ouverture : booking_cancel_info (requireAdmin) lit EN DIRECT sur le PI
// Stripe le plafond remboursable + le déjà-remboursé (M1 — jamais le snapshot),
// le caution_status (alerte M6 si `charged`) et le total séjour payé
// (pricing_blocks.total_amount, TTC séjour, caution NON agrégée).
//
// TYPE D'ANNULATION (décision produit Dimitri 2026-07-20) — radio en tête de
// modale, OBLIGATOIRE avant le reste du formulaire :
//   - « Annulation du voyageur » (cancel_origin='guest') ;
//   - « Annulation de notre côté » (cancel_origin='host').
// ⚠️ Depuis le chantier 3b, le type ne pilote PLUS le préremplissage du
// montant : il ne sert QU'À l'audit (pricing_audit_log via
// _appendCancelOriginToAudit) et à la variante du gabarit email voyageur
// (buildCancelOriginLine). cancel_origin est transmis à booking_cancel_paid
// comme MÉTADONNÉE D'AUDIT UNIQUEMENT (aucun changement de logique serveur ;
// absent/inconnu → défaut 'host'). *Coexistence assumée (Option B du cadrage
// 3b) — gate produit Dimitri : la case « Rembourser » ci-dessous devient
// l'unique porte du remboursement, le type reste pour la trace/l'email.*
//
// REMBOURSEMENT (décision Dimitri chantier 3b) — case « Rembourser le
// voyageur » DÉCOCHÉE PAR DÉFAUT (« pas de remboursement accidentel ») :
//   - décochée → amount = 0 € (champ montant masqué) → refund_step
//     skipped_zero_amount côté serveur ;
//   - cochée → montant PRÉREMPLI = MIN(total_amount_eur, refundable_eur)
//     (clampé au remboursable Stripe pour ne jamais proposer un défaut
//     invalide), puis champ 100 % ÉDITABLE de 0 au plafond (remboursement
//     partiel autorisé, D2-B — plafond dur serveur amount_exceeds_refundable).
// Toggle de la case (0 ↔ prérempli) → DÉCOCHE la double-confirmation
// (re-lecture forcée avant soumission d'un mouvement d'argent). Aucun barème
// auto (remboursement 100 % MANUEL). Raison ≥ 10 caractères + case de double
// confirmation. Confirm → booking_cancel_paid (orchestration 1 clic) —
// TOUJOURS keyé request_id (chemin d'écriture argent inchangé). La libération
// de l'empreinte de caution reste AUTOMATIQUE côté serveur, que la case
// « Rembourser » soit cochée ou non.

import { useState, useEffect } from "react"
import { Ban, AlertTriangle } from "lucide-react"
import { supabase } from "../supabase"
import { DS } from "../lib/ui"
import { formatDateFR, fmtE } from "../lib/format"

import useEchapPourFermer from "../lib/useEchapPourFermer"
/**
 * @param {object} props
 * @param {object} props.request — demande directe (Demandes) OU objet minimal
 *   { id: null, guest_first_name, guest_last_name, start_date, end_date }
 *   construit depuis le bloc (calendrier) — sert uniquement à l'affichage
 *   du header quand `pricingBlockId` est fourni.
 * @param {string} [props.pricingBlockId] — mode calendrier : le serveur résout
 *   la demande liée (booking_cancel_info renvoie `request_id`).
 * @param {string} props.logementNom
 * @param {function} props.onClose
 * @param {function} props.onDone — appelé au clic « Fermer » après succès.
 */
export default function BookingCancelModal({ request, pricingBlockId, logementNom, onClose, onDone }) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)

  const [info, setInfo] = useState(null) // payload booking_cancel_info
  const [infoError, setInfoError] = useState(null)
  // Type d'annulation (2026-07-20) : null tant que l'admin n'a pas choisi — le
  // choix est OBLIGATOIRE avant le reste (case « Rembourser »/raison/confirm
  // masqués). Sert UNIQUEMENT à l'audit + l'email (plus au préremplissage 3b).
  const [cancelOrigin, setCancelOrigin] = useState(null) // 'guest' | 'host' | null
  // Chantier 3b — case « Rembourser le voyageur » DÉCOCHÉE par défaut : unique
  // porte du remboursement. Décochée → amount 0 (champ masqué) ; cochée →
  // amount prérempli au total séjour payé, clampé au remboursable, éditable.
  // Motif d'annulation HÔTE (Dimitri 2026-08-03) : '' tant que rien n'est
  // choisi — le serveur REFUSE une annulation hôte sans motif (400).
  const [cancelMotif, setCancelMotif] = useState('')
  const [refundChecked, setRefundChecked] = useState(false)
  const [amount, setAmount] = useState('0')
  const [raison, setRaison] = useState('')
  // Feature 3a — motif d'annulation dans l'email voyageur, MIROIR EXACT du motif
  // de rejet (BookingRequestsView.jsx, checkbox booking-validation-reason-public).
  // Le motif (`raison`) est TOUJOURS enregistré en interne ; cette case (décochée
  // par défaut) mémorise le choix « inclure le motif dans l'email voyageur ».
  // ⚠️ L'exposition dans l'email se fera au CHANTIER B (pose du message 19) : en
  // 3a on ne fait que transmettre `reason_public` au serveur, qui le persiste.
  const [reasonPublic, setReasonPublic] = useState(false)
  const [confirmChecked, setConfirmChecked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [result, setResult] = useState(null) // réponse booking_cancel_paid

  useEffect(() => {
    let aborted = false
    ;(async () => {
      try {
        // MINEUR-2 (pessimist #994) — garde anti-mésusage double-clé : les 2
        // call-sites actuels sont disjoints (Demandes = request.id, calendrier
        // = pricingBlockId). Si un appelant fournissait les DEUX, priorité
        // DOCUMENTÉE : `request.id` GAGNE — pour le fetch info ci-dessous
        // COMME pour le submit (resolvedRequestId), afin que fetch et submit
        // soient toujours keyés sur la même demande. pricingBlockId ignoré.
        if (request?.id && pricingBlockId) {
          // eslint-disable-next-line no-console
          console.error(
            '[BookingCancelModal] request.id ET pricingBlockId fournis — request.id prioritaire (fetch + submit), pricingBlockId ignoré.',
          )
        }
        const { data: sess } = await supabase.auth.getSession()
        const token = sess?.session?.access_token
        if (!token) throw new Error('Pas de session admin active.')
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(
            // request.id d'abord (priorité documentée ci-dessus) ; sinon mode
            // calendrier : résolution serveur par pricing_block_id.
            request?.id
              ? { action: 'booking_cancel_info', request_id: request.id }
              : { action: 'booking_cancel_info', pricing_block_id: pricingBlockId },
          ),
        })
        const json = await res.json()
        if (aborted) return
        if (!res.ok || !json.ok) throw new Error(json.error || 'info_failed')
        setInfo(json)
        // Pas de préremplissage ici : le montant reste 0 (case « Rembourser »
        // décochée) tant que l'admin ne coche pas explicitement (3b).
      } catch (e) {
        if (!aborted) setInfoError(e.message)
      }
    })()
    return () => { aborted = true }
  }, [request?.id, pricingBlockId])

  // request_id effectif pour le submit : celui de la demande (Demandes) OU
  // celui résolu par booking_cancel_info (mode pricingBlockId). Même priorité
  // que le fetch info (request.id gagne — garde MINEUR-2 ci-dessus) : fetch et
  // submit sont toujours keyés sur la même demande. Le chemin d'écriture
  // argent booking_cancel_paid reste keyé request_id UNIQUEMENT.
  const resolvedRequestId = request?.id || info?.request_id || null

  const refundableEur = Number(info?.refundable_eur ?? 0)

  // 🔴 PLANCHER TAXE DE SÉJOUR — servi PAR LE SERVEUR (info.cancel_motifs), qui
  // le recalcule et le REFUSE de son côté (422). Ce qui est affiché ici n'est
  // qu'une aide : le navigateur ne décide jamais d'un plancher d'argent.
  const motifInfo = (info?.cancel_motifs && cancelMotif)
    ? info.cancel_motifs[cancelMotif]
    : null
  const taxeEur = Number(info?.taxe_sejour_eur ?? 0)
  // 🔴 LE PLANCHER NE DÉPEND PAS DU MOTIF (correction de revue #1170).
  // Le dériver de `motifInfo` le rendait NUL sur « Annulation du voyageur »,
  // où aucun motif n'est choisi : le bouton restait actif, rien ne signalait la
  // taxe, et le serveur refusait en 422 sans que l'admin sache quoi corriger —
  // sur le cas le PLUS FRÉQUENT (voyageur qui se désiste, logement non
  // remboursable). Le plancher est JURIDIQUE, pas motivé : il vaut la taxe dès
  // qu'elle existe, quel que soit le type d'annulation.
  // Le plafond remboursable reste la seule borne matérielle.
  const floorEur = Math.min(taxeEur, Number(info?.refundable_eur ?? 0))

  // Chantier 3b — préremplissage clampé (ARGENT RISK #1) : défaut = total
  // séjour payé, MAIS jamais au-dessus du remboursable Stripe (si un refund
  // partiel a déjà eu lieu, total_amount > refundable_eur → un défaut brut
  // serait invalide/trop haut). Total absent/0 → on retombe sur le remboursable
  // (proxy fidèle du « total payé restant »). Le plafond dur reste serveur.
  // Le MOTIF fait foi quand il est choisi : le serveur a calculé le montant
  // que le contrat prévoit (intégral / politique du logement / prorata). À
  // défaut (annulation voyageur, ou motif pas encore choisi), on garde le
  // comportement historique = total payé clampé au remboursable.
  // ⚠️ DEUX GESTES, DEUX INTENTIONS (correction : la 1re version les confondait).
  //   · CHOISIR UN MOTIF  → le montant vient du motif (`chooseMotif`), parce que
  //     l'article du contrat vient d'être désigné.
  //   · COCHER « Rembourser » → l'admin décide de rembourser MALGRÉ la
  //     suggestion : le défaut utile reste le total payé, pas la suggestion
  //     qu'il est justement en train d'outrepasser.
  // Les mélanger faisait retomber la case sur 0 € après un motif « du fait du
  // voyageur », et rendait le geste inutile.
  const computeRefundPrefill = () => {
    // 🔴 ANNULATION DU VOYAGEUR : on prérègle sur ce que le SERVEUR a calculé
    // pour sa politique (art. 11.1), pas sur le total payé (Dimitri
    // 2026-08-04 : « mon cas courant c'est non remboursable — donc la taxe
    // seule. Proposer le total m'amènerait à rembourser par erreur »).
    // Aucun motif n'est demandé sur ce parcours, mais le serveur a déjà
    // calculé `guest_fault` : c'est exactement l'article applicable.
    if (cancelOrigin === 'guest') {
      const suggestion = Number(info?.cancel_motifs?.guest_fault?.amount)
      if (Number.isFinite(suggestion)) return Math.min(suggestion, refundableEur)
    }
    const totalEur = Number(info?.total_amount_eur)
    const base = Number.isFinite(totalEur) && totalEur > 0 ? totalEur : refundableEur
    return Math.min(base, refundableEur)
  }

  // Choisir (ou changer) le motif reprérémplit le montant et FORCE une
  // re-lecture : le montant proposé vient de changer de fondement juridique.
  const chooseMotif = (next) => {
    setCancelMotif(next)
    setConfirmChecked(false)
    const info2 = info?.cancel_motifs ? info.cancel_motifs[next] : null
    if (info2 && Number.isFinite(Number(info2.amount))) {
      const suggested = Math.min(Number(info2.amount), refundableEur)
      setRefundChecked(suggested > 0)
      setAmount(String(suggested))
    }
  }

  // Choix (ou changement) du type d'annulation → NE touche PLUS le montant (3b :
  // c'est la case « Rembourser » qui pilote le remboursement). On décoche
  // néanmoins la confirmation : le type infléchit l'audit + l'email voyageur,
  // l'admin doit re-lire avant de soumettre.
  const chooseOrigin = (next) => {
    setCancelOrigin(next)
    setConfirmChecked(false)
    // Le motif n'a de sens que pour une annulation de NOTRE côté : changer de
    // type doit l'effacer, jamais le laisser traîner d'un choix précédent.
    setCancelMotif('')
  }

  // Chantier 3b — bascule de la case « Rembourser » : (dé)coche → (dé)prérempli.
  // Un toggle change le MONTANT (0 ↔ prérempli) → DÉCOCHE la confirmation
  // (re-lecture forcée avant tout mouvement d'argent). Décochée → 0 € (aucun
  // remboursement, défaut anti-accident). Cochée → prérempli clampé, éditable.
  const toggleRefund = (checked) => {
    setRefundChecked(checked)
    setAmount(checked ? String(computeRefundPrefill()) : '0')
    setConfirmChecked(false)
  }

  const amountNum = Number(amount)
  const effectiveAmount = refundChecked ? amountNum : 0
  const amountValid = Number.isFinite(amountNum) && amountNum >= 0 &&
    (!info || amountNum <= refundableEur)
  // 🔴 Plancher taxe : BLOQUANT (décision Dimitri). Vaut aussi case décochée —
  // « ne rien rembourser » n'est pas permis quand une taxe de séjour existe.
  const floorRespected = !(floorEur > 0) || effectiveAmount + 1e-9 >= floorEur
  const motifOk = cancelOrigin !== 'host' || !!cancelMotif
  const canSubmit = !!info && info.cancellable && !!resolvedRequestId &&
    !!cancelOrigin && motifOk && amountValid && floorRespected &&
    raison.trim().length >= 10 && confirmChecked && !busy && !result

  // Micro-UX recette 2026-07 (#993, porté ici à l'extraction #994) : le bouton
  // « Annuler définitivement » grisé sans indice laissait l'admin sans piste.
  // Liste dynamique de ce qui manque — vide quand tout est rempli (ex. pendant
  // l'envoi : busy grise le bouton mais rien ne « manque » → pas de hint).
  // Tant que le TYPE n'est pas choisi, le hint ne liste QUE le type — le reste
  // du formulaire (case « Rembourser »/montant/raison/confirm) n'est pas encore
  // rendu (il n'apparaît qu'après la radio). Une fois le type choisi, l'ordre
  // du hint suit l'ordre des champs à l'écran (montant → raison → case). Le
  // « montant invalide » n'est listé QUE si la case « Rembourser » est cochée
  // (sinon le montant est un 0 implicite toujours valide).
  // Traduction des refus SERVEUR en français : `below_tourist_tax_floor` brut
  // ne dit rien à l'admin (constat de revue #1170).
  const messageErreur = (code, payload) => {
    if (code === 'below_tourist_tax_floor') {
      const f = Number(payload?.floor_eur ?? 0)
      return `Montant trop bas : la taxe de séjour (${fmtE(f)}) doit être remboursée `
        + `quelle que soit la politique d'annulation.`
    }
    if (code === 'cancel_motif_required') return "Choisissez le motif de l'annulation."
    // P-2.45 : Stripe a refusé le remboursement — rien n'a été annulé.
    if (code === 'stripe_refund_refused') {
      return "Stripe a refusé le remboursement : aucun argent n'est parti. Vérifiez le paiement dans Stripe, "
        + "ou annulez avec le montant minimum (la taxe de séjour) et remboursez le voyageur hors de l'application."
    }
    return null
  }

  const missingItems = []
  if (info && info.cancellable && !result) {
    if (!cancelOrigin) {
      missingItems.push("choisir le type d'annulation")
    } else {
      if (cancelOrigin === 'host' && !cancelMotif) missingItems.push("choisir le motif de l'annulation")
      if (refundChecked && !amountValid) missingItems.push(`montant invalide (max ${fmtE(refundableEur)})`)
      if (!floorRespected) missingItems.push(`taxe de séjour à rembourser : minimum ${fmtE(floorEur)}`)
      if (raison.trim().length < 10) missingItems.push('raison (min 10 caractères)')
      if (!confirmChecked) missingItems.push('cocher la case de confirmation')
    }
  }
  const showMissingHint = !canSubmit && missingItems.length > 0

  const submit = async () => {
    if (!canSubmit) return
    setBusy(true)
    setSubmitError(null)
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) throw new Error('Pas de session admin active.')
      const res = await fetch('/api/beds24-mapping', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'booking_cancel_paid',
          request_id: resolvedRequestId,
          // Chantier 3b — case décochée → amount 0 (aucun remboursement) ;
          // cochée → montant saisi (prérempli clampé, éditable 0→plafond).
          amount_eur: amountNum,
          raison: raison.trim(),
          // Feature 3a — choix « inclure le motif dans l'email voyageur »
          // (miroir du rejet). Le serveur persiste TOUJOURS le motif ;
          // reason_public mémorise l'affichage (exposition email = chantier B).
          reason_public: reasonPublic,
          // MÉTADONNÉE D'AUDIT UNIQUEMENT (deux types, 2026-07-20) : le serveur
          // ne change aucune logique (défaut 'host' si absent/inconnu).
          cancel_origin: cancelOrigin,
          // Motif hôte — le serveur le REFUSE absent (400) et s'en sert pour
          // justifier le montant dans l'audit et dans l'email voyageur.
          ...(cancelOrigin === 'host' && cancelMotif ? { cancel_motif: cancelMotif } : {}),
        }),
      })
      const json = await res.json()
      if (!res.ok || !json.ok) {
        // Refus SERVEUR traduit en français quand on sait le faire : un code
        // brut (`below_tourist_tax_floor`) laissait l'admin sans piste.
        throw new Error(messageErreur(json.error, json) || json.error || 'cancel_failed')
      }
      setResult(json)
    } catch (e) {
      setSubmitError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const cautionCharged = info?.caution_status === 'charged'
  const hasTotalPaid = info && info.total_amount_eur != null

  return (
    <div
      data-testid="booking-cancel-modal"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.4)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose() }}
    >
      <div className="fbz-modal-sheet" style={{
        background: DS.card, borderRadius: 14, padding: 20, width: '90%', maxWidth: 520,
        fontFamily: DS.fontBody, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 0, fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark }}>
          <Ban size={18} strokeWidth={2.2} color={DS.dangerText}/> Annuler la réservation
        </h3>
        <div style={{ marginBottom: 12, fontSize: 13, color: DS.textSecondary }}>
          {request.guest_first_name} {request.guest_last_name} · {logementNom} · {formatDateFR(request.start_date)} → {formatDateFR(request.end_date)}
        </div>

        {infoError && (
          <div data-testid="booking-cancel-info-error" style={{ padding: '9px 12px', background: DS.dangerBg, color: DS.dangerText, borderRadius: 8, fontSize: 12.5, marginBottom: 10 }}>
            Impossible de lire l'état du paiement : {infoError}
          </div>
        )}
        {!info && !infoError && (
          <div style={{ color: DS.textSecondary, fontSize: 12, marginBottom: 10 }}>Lecture du paiement Stripe…</div>
        )}

        {info && !result && (
          <>
            {!info.cancellable && (
              <div data-testid="booking-cancel-not-cancellable" style={{ padding: '9px 12px', background: DS.dangerBg, color: DS.dangerText, borderRadius: 8, fontSize: 12.5, marginBottom: 10 }}>
                Cette réservation n'est pas annulable (statut « {info.statut} » ou séjour déjà commencé) — traitement manuel.
              </div>
            )}

            <div style={{ fontSize: 12.5, color: DS.textPrimary, background: DS.pageBg, borderRadius: 8, padding: 10, marginBottom: 10 }}>
              {hasTotalPaid && (
                <>
                  Total séjour payé : <strong data-testid="booking-cancel-total-paid">{fmtE(info.total_amount_eur || 0)}</strong>
                  {' '}· </>
              )}
              Séjour encaissé : <strong>{fmtE(info.amount_received_eur || 0)}</strong>
              {' '}· Déjà remboursé : <strong>{fmtE(info.already_refunded_eur || 0)}</strong>
              {' '}· Remboursable : <strong data-testid="booking-cancel-refundable">{fmtE(info.refundable_eur || 0)}</strong>
              {/* 🔴 « libérée automatiquement » était affirmé SANS CONDITION —
                  corrigé le 2026-08-05. Au-delà de 4 nuits, la caution n'est
                  pas une empreinte mais un DÉBIT RÉEL (lib/caution-mode.js) :
                  il n'y a rien à « libérer », il y a de l'argent à rembourser.
                  On lit l'état Stripe réel (`caution_status`), pas une
                  supposition. */}
              <div style={{ marginTop: 4, fontSize: 11, color: DS.textMuted }}>
                {cautionCharged
                  ? "Le total séjour n'inclut pas la caution : elle a été DÉBITÉE (séjour long). Elle n'est jamais remboursée ici — c'est une décision séparée."
                  : "Le total séjour n'inclut pas la caution (empreinte séparée, jamais remboursée ici — libérée automatiquement)."}
              </div>
            </div>

            {/* Rappel TAXE — hors du bloc « motif » : il vaut aussi pour une
                annulation du voyageur, où aucun motif n'est demandé. */}
            {/* Politique non figée à la signature : l'avertissement vaut pour
                LES DEUX types d'annulation — sur une annulation du voyageur
                aussi, la suggestion repose sur une politique qu'il n'a
                peut-être pas signée. */}
            {info.cancellable && info.politique_source === 'politique_vivante' && (
              <div data-testid="booking-cancel-politique-source" style={{ marginBottom: 10, fontSize: 11.5, color: DS.textSecondary }}>
                Aucune politique n'a été figée à la signature pour cette demande :
                la suggestion s'appuie sur la politique <strong>actuelle</strong> du
                logement ({info.politique_annulation}), que le voyageur n'a peut-être
                pas signée. À vérifier avant de valider le montant.
              </div>
            )}
            {info.cancellable && taxeEur > 0 && (
              <div data-testid="booking-cancel-taxe-floor" style={{ marginBottom: 10, background: '#fdf6e7', borderRadius: 8, padding: 10, fontSize: 12, color: '#8a6d1f' }}>
                <strong>Taxe de séjour : {fmtE(taxeEur)}</strong> — remboursée quelle que soit
                la politique d'annulation (collectée pour le compte de la commune).
                {/* Le montant OPPOSABLE peut être inférieur à la taxe si un
                    remboursement partiel a déjà épuisé le paiement : on affiche
                    alors le plancher RÉEL, jamais deux chiffres contradictoires
                    (bandeau vs hint). Et si plus rien n'est remboursable, on le
                    dit — l'obligation ne disparaît pas en silence. */}
                {floorEur > 0
                  ? <> Montant minimum remboursable : <strong>{fmtE(floorEur)}</strong>.</>
                  : ' Plus rien n\'est remboursable sur ce paiement — à régulariser hors application.'}
              </div>
            )}
            {info.cancellable && (
              // Type d'annulation (2026-07-20) — choix OBLIGATOIRE avant le
              // reste : le formulaire (case « Rembourser »/montant/raison/
              // confirmation) n'apparaît qu'après. Le type ne pilote QUE
              // l'audit + l'email voyageur (plus le préremplissage, 3b).
              <div data-testid="booking-cancel-origin" style={{ marginBottom: 10, background: DS.pageBg, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 12, marginBottom: 6, color: '#444', fontWeight: 600 }}>
                  Type d'annulation
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6, fontSize: 12.5, color: DS.textPrimary, cursor: 'pointer' }}>
                  <input
                    data-testid="booking-cancel-origin-guest"
                    type="radio"
                    name="booking-cancel-origin"
                    checked={cancelOrigin === 'guest'}
                    onChange={() => chooseOrigin('guest')}
                    style={{ marginTop: 2 }}
                  />
                  <span><strong>Annulation du voyageur</strong></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: DS.textPrimary, cursor: 'pointer' }}>
                  <input
                    data-testid="booking-cancel-origin-host"
                    type="radio"
                    name="booking-cancel-origin"
                    checked={cancelOrigin === 'host'}
                    onChange={() => chooseOrigin('host')}
                    style={{ marginTop: 2 }}
                  />
                  <span><strong>Annulation de notre côté</strong></span>
                </label>
                {cancelOrigin && (
                  <div data-testid="booking-cancel-origin-help" style={{ marginTop: 8, fontSize: 12, color: DS.textSecondary }}>
                    {cancelOrigin === 'guest'
                      ? 'Annulation à l\'initiative du voyageur. Les dates seront rouvertes à la vente.'
                      : 'Annulation à notre initiative. Les dates seront rouvertes à la vente.'}
                  </div>
                )}

                {/* MOTIF (annulation de NOTRE côté) — le contrat v2 distingue
                    trois situations aux conséquences opposées sur l'argent.
                    Aucun défaut : tant qu'aucun motif n'est choisi, le montant
                    n'est pas préréglé et l'envoi reste bloqué. */}
                {cancelOrigin === 'host' && (
                  <div data-testid="booking-cancel-motif" style={{ marginTop: 10, borderTop: `1px solid ${DS.border || '#e5e7eb'}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 12, marginBottom: 6, color: '#444', fontWeight: 600 }}>
                      Motif de l'annulation
                    </div>
                    {[
                      { v: 'host_fault', l: 'De mon fait', h: 'Dégât des eaux, travaux, indisponibilité — remboursement intégral (contrat art. 11.2).' },
                      { v: 'guest_fault', l: 'Du fait du voyageur', h: 'Dossier incomplet, formalités non accomplies — politique du logement (art. 11.1 et 5).' },
                      { v: 'force_majeure', l: 'Force majeure', h: 'Remboursement au prorata de la période non exécutée (art. 12).' },
                    ].map((o) => (
                      <label key={o.v} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6, fontSize: 12.5, color: DS.textPrimary, cursor: 'pointer' }}>
                        <input
                          data-testid={`booking-cancel-motif-${o.v}`}
                          type="radio"
                          name="booking-cancel-motif"
                          checked={cancelMotif === o.v}
                          onChange={() => chooseMotif(o.v)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <strong>{o.l}</strong>
                          <span style={{ display: 'block', color: DS.textSecondary, fontSize: 11.5 }}>{o.h}</span>
                        </span>
                      </label>
                    ))}
                    {motifInfo && motifInfo.computable === false && (
                      <div data-testid="booking-cancel-motif-warn" style={{ marginTop: 6, fontSize: 11.5, color: '#8a6d1f' }}>
                        Politique personnalisée : aucun montant ne peut en être déduit
                        automatiquement. À saisir manuellement.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {cautionCharged && (
              // M6 — caution réellement ENCAISSÉE (long séjour) : l'annulation
              // ne la rembourse JAMAIS automatiquement — décision séparée via
              // le bouton « Rembourser caution » existant.
              <div data-testid="booking-cancel-caution-charged" style={{ display: 'flex', gap: 8, padding: '9px 12px', background: DS.dangerBg, color: DS.dangerText, borderRadius: 8, borderLeft: `3px solid ${DS.danger}`, fontSize: 12.5, marginBottom: 10 }}>
                <AlertTriangle size={15} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }}/>
                <span>
                  <strong>Caution débitée détenue.</strong> L'annulation ne la rembourse pas :
                  utilisez « Rembourser caution » (décision séparée) après l'annulation.
                </span>
              </div>
            )}

            {/* Type d'annulation (2026-07-20) : le reste du formulaire n'apparaît
                qu'après le choix du type. Chantier 3b : la case « Rembourser »
                (décochée par défaut) est l'unique porte du remboursement. */}
            {cancelOrigin && (<>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: refundChecked ? 8 : 12, fontSize: 12.5, color: '#444', cursor: 'pointer' }}>
              <input
                data-testid="booking-cancel-refund-check"
                type="checkbox"
                checked={refundChecked}
                onChange={(e) => toggleRefund(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              {/* 🔴 « dans tous les cas » était FAUX dès 5 nuits — corrigé le
                  2026-08-05. Au-delà de 4 nuits (CAUTION_HOLD_MAX_NIGHTS), la
                  caution est prélevée pour de bon : promettre une libération
                  automatique exposait à un litige (« vous m'aviez dit que
                  c'était libéré »). Le texte exact existait déjà 20 lignes plus
                  haut, dans l'encart « Caution débitée détenue ». */}
              <span>
                <strong>Rembourser le voyageur</strong> — sinon aucun remboursement
                {cautionCharged
                  ? " (la caution, elle, a été débitée : son remboursement est une décision séparée)."
                  : " (l'empreinte de caution est libérée automatiquement)."}
              </span>
            </label>

            {refundChecked && (
              <label style={{ display: 'block', marginBottom: 8 }}>
                <div style={{ fontSize: 12, marginBottom: 4, color: '#444' }}>
                  Montant à rembourser (€) — prérempli au total séjour, plafond {fmtE(refundableEur)}, éditable
                </div>
                <input
                  data-testid="booking-cancel-amount"
                  type="number"
                  min="0"
                  max={refundableEur}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${amountValid ? '#ccc' : DS.danger}`, fontFamily: DS.fontBody, fontSize: 13 }}
                />
              </label>
            )}

            <label style={{ display: 'block', marginBottom: 8 }}>
              <div style={{ fontSize: 12, marginBottom: 4, color: '#444' }}>Raison de l'annulation (min 10 caractères)</div>
              <textarea
                data-testid="booking-cancel-raison"
                value={raison}
                onChange={(e) => setRaison(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', fontFamily: DS.fontBody, fontSize: 13 }}
                placeholder="Ex : Demande du voyageur du 14/07 par email"
              />
            </label>

            {/* Feature 3a — miroir EXACT du motif de rejet (BookingRequestsView) :
                le motif est TOUJOURS enregistré en interne ; cette case (décochée
                par défaut) mémorise le choix « inclure dans l'email voyageur ».
                Exposition email = chantier B (message 19) — ici on ne fait que
                transmettre le choix au serveur, qui le persiste. */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, fontSize: 12, color: '#444' }}>
              <input
                data-testid="booking-cancel-reason-public"
                type="checkbox"
                checked={reasonPublic}
                onChange={(e) => setReasonPublic(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              Inclure le motif dans l'email voyageur (sinon générique)
            </label>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, fontSize: 12, color: '#444' }}>
              <input
                data-testid="booking-cancel-confirm-check"
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                Je confirme l'annulation définitive : remboursement Stripe, libération des dates
                (réouverture Beds24), annulation de l'empreinte de caution et email au voyageur.
              </span>
            </label>
            </>)}

            {submitError && (
              <div data-testid="booking-cancel-submit-error" style={{ padding: '9px 12px', background: DS.dangerBg, color: DS.dangerText, borderRadius: 8, fontSize: 12.5, marginBottom: 10 }}>
                Échec : {submitError} — rien n'a été modifié si l'erreur vient du remboursement (tout-ou-rien). Re-cliquez pour reprendre.
              </div>
            )}
          </>
        )}

        {result && (
          <div data-testid="booking-cancel-result" style={{ padding: '9px 12px', background: DS.pageBg, borderRadius: 8, fontSize: 12.5, marginBottom: 10, color: DS.textPrimary }}>
            <div><strong>Réservation annulée.</strong>{result.resumed ? ' (reprise d\'une annulation interrompue)' : ''}</div>
            <div>Remboursement : {result.refund?.refunded_eur > 0 ? `${fmtE(result.refund.refunded_eur)} (${result.refund.step})` : 'aucun'}</div>
            <div>Caution : {result.caution?.step === 'cancel_requested' ? 'empreinte annulée' : result.caution?.step === 'charged_alert' ? '⚠️ caution débitée détenue — rembourser séparément' : result.caution?.step === 'cancel_failed' ? '⚠️ échec annulation empreinte — vérifier Stripe' : 'aucune action'}</div>
            <div>Beds24 : {result.reopen?.status === 'reopened' ? 'dates rouvertes' : result.reopen?.status === 'not_pushed' ? 'rien à rouvrir (jamais fermé)' : `⚠️ ${result.reopen?.status || 'inconnu'} — voir alertes`}</div>
          </div>
        )}

        {showMissingHint && (
          <div
            data-testid="booking-cancel-missing-hint"
            style={{ fontSize: 11.5, color: DS.textMuted, marginTop: 12, textAlign: 'right' }}
          >
            Pour activer : {missingItems.join(' · ')}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: showMissingHint ? 8 : 16 }}>
          <button
            className="fbz-press"
            onClick={result ? onDone : onClose}
            disabled={busy}
            data-testid="booking-cancel-close"
            style={{ padding: '8px 16px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.textSecondary, fontWeight: 600, cursor: 'pointer', fontFamily: DS.fontBody }}
          >
            {result ? 'Fermer' : 'Retour'}
          </button>
          {!result && (
            <button
              className="fbz-press"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="booking-cancel-submit"
              style={{
                padding: '8px 16px', borderRadius: DS.radiusControl, border: 'none',
                background: canSubmit ? DS.danger : DS.borderHover,
                color: '#fff', fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed', fontFamily: DS.fontBody,
              }}
            >
              {/* Le clic final PORTE le montant — l'admin voit ce qu'il rembourse
                  au moment d'appuyer. Sans type choisi → libellé neutre ; case
                  « Rembourser » décochée (ou montant 0) → « aucun remboursement ». */}
              {busy
                ? '…'
                : !cancelOrigin
                  ? 'Annuler définitivement'
                  : refundChecked && amountValid && amountNum > 0
                    ? `Annuler définitivement — rembourser ${fmtE(amountNum)}`
                    : 'Annuler définitivement — aucun remboursement'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
